import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from './components/Layout';
import { Home } from './pages/Home';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { BrandOnboarding } from './pages/onboarding/BrandOnboarding';
import { OrganizerOnboarding } from './pages/onboarding/OrganizerOnboarding';
import { BrandDashboard } from './pages/dashboard/BrandDashboard';
import { OrganizerDashboard } from './pages/dashboard/OrganizerDashboard';
import { MatchDetails } from './pages/dashboard/MatchDetails';
import { MatchResultsPage } from './pages/dashboard/MatchResultsPage';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { LoginTroubleshooting } from './pages/LoginTroubleshooting';
import { MessagesPage } from './pages/dashboard/MessagesPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DraftProfileProvider } from './context/DraftProfileContext';
import { HelpChat } from './components/HelpChat';
import { RegistrationDebugHelper } from './components/RegistrationDebugHelper';
import { initializeCollaborations } from './services/collaborationService';
import { initEmailJS } from './services/emailService';
import { initializeDemoData } from './services/demoDataService';
import { TechCursor } from './components/TechEffects';
import './styles/tech-effects.css';
import { DemoLogin } from './pages/DemoLogin';
import { SuperAdminDashboard } from './pages/admin/SuperAdminDashboard';
// Protected route component
const ProtectedRoute = ({
  children,
  requiredType
}: {
  children: React.ReactNode;
  requiredType?: 'brand' | 'organizer' | 'admin';
}) => {
  const {
    currentUser
  } = useAuth();
  if (!currentUser) {
    return <Navigate to="/login" replace data-id="element-0" />;
  }
  if (requiredType && currentUser.type !== requiredType) {
    if (currentUser.type === 'brand') {
      return <Navigate to="/dashboard/brand" replace data-id="element-1" />;
    } else if (currentUser.type === 'organizer') {
      return <Navigate to="/dashboard/organizer" replace data-id="element-2" />;
    } else {
      return <Navigate to="/admin" replace data-id="element-3" />;
    }
  }
  return <>{children}</>;
};
const AppRoutes = () => {
  useEffect(() => {
    initializeCollaborations();
    initializeDemoData();
    initEmailJS();
  }, []);
  return <Routes data-id="element-4">
      <Route path="/" element={<Home data-id="element-6" />} data-id="element-5" />

      {/* Onboarding */}
      <Route path="/onboarding/brand" element={<BrandOnboarding data-id="element-8" />} data-id="element-7" />
      <Route path="/onboarding/organizer" element={<OrganizerOnboarding data-id="element-10" />} data-id="element-9" />

      {/* Auth */}
      <Route path="/login" element={<Login data-id="element-12" />} data-id="element-11" />
      <Route path="/login/help" element={<LoginTroubleshooting data-id="element-14" />} data-id="element-13" />
      <Route path="/register" element={<Register data-id="element-16" />} data-id="element-15" />

      {/* Brand Dashboard */}
      <Route path="/dashboard/brand" element={<ProtectedRoute requiredType="brand" data-id="element-18">
            <BrandDashboard data-id="element-19" />
          </ProtectedRoute>} data-id="element-17" />

      {/* Organizer Dashboard */}
      <Route path="/dashboard/organizer" element={<ProtectedRoute requiredType="organizer" data-id="element-21">
            <OrganizerDashboard data-id="element-22" />
          </ProtectedRoute>} data-id="element-20" />

      {/* Shared Dashboard Routes */}
      <Route path="/dashboard/matches" element={<ProtectedRoute data-id="element-24">
            <MatchResultsPage data-id="element-25" />
          </ProtectedRoute>} data-id="element-23" />
      <Route path="/dashboard/matches/:matchId" element={<ProtectedRoute data-id="element-27">
            <MatchDetails data-id="element-28" />
          </ProtectedRoute>} data-id="element-26" />
      <Route path="/dashboard/messages" element={<ProtectedRoute data-id="element-30">
            <MessagesPage data-id="element-31" />
          </ProtectedRoute>} data-id="element-29" />

      {/* Admin */}
      <Route path="/admin" element={<ProtectedRoute requiredType="admin" data-id="element-33">
            <AdminDashboard data-id="element-34" />
          </ProtectedRoute>} data-id="element-32" />

      {/* Demo Login Route */}
      <Route path="/demo" element={<DemoLogin data-id="element-36" />} data-id="element-35" />

      {/* Super Admin Route */}
      <Route path="/admin/super" element={<ProtectedRoute allowedRoles={['super_admin']} data-id="element-38">
            <SuperAdminDashboard data-id="element-39" />
          </ProtectedRoute>} data-id="element-37" />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace data-id="element-41" />} data-id="element-40" />
    </Routes>;
};
export function App() {
  return <AuthProvider data-id="element-42">
      <BrowserRouter data-id="element-43">
        <AppRoutes data-id="element-44" />
        <HelpChat data-id="element-45" />
        <TechCursor data-id="element-46" />
        {typeof process !== 'undefined' && process.env?.NODE_ENV === 'development' && <RegistrationDebugHelper data-id="element-47" />}
      </BrowserRouter>
    </AuthProvider>;
}